import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';
import MobileStickyCTA from '@/components/feature/MobileStickyCTA';

export default function AreYouAddictedPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilldrugmart.com';

  useSEO({
    title: 'Addiction Self-Assessment | Signs & Symptoms - Thornhill',
    description: 'Confidential addiction self-assessment and warning signs guide in Thornhill. Learn to recognize signs of drug addiction, opioid dependency, and substance abuse. Professional addiction assessment and treatment available. Serving Richmond Hill, Vaughan, and North York. Expert methadone and Suboxone clinic. Call (905) 709-9985 for confidential help.',
    keywords: 'am I addicted Thornhill, addiction assessment Thornhill, signs of addiction Richmond Hill, drug addiction symptoms Vaughan, opioid addiction signs North York, addiction self-test, substance abuse assessment, addiction warning signs, drug dependency symptoms, addiction evaluation Thornhill, addiction help Richmond Hill, addiction screening Vaughan, substance use disorder assessment',
    canonical: `${siteUrl}/are-you-addicted`,
    ogType: 'article',
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      "name": "Are You Addicted? - Addiction Self-Assessment & Warning Signs",
      "description": "Comprehensive self-assessment guide to help identify signs of addiction, understand warning symptoms, and determine when to seek professional help for substance use disorders in Thornhill, Richmond Hill, Vaughan, and North York.",
      "url": `${siteUrl}/are-you-addicted`,
      "about": {
        "@type": "MedicalCondition",
        "name": "Substance Use Disorder",
        "alternateName": ["Addiction", "Drug Dependency", "Substance Abuse"],
        "description": "A chronic disease characterized by compulsive substance use despite harmful consequences"
      },
      "mainEntity": {
        "@type": "MedicalBusiness",
        "name": "Thornhill Drug-Mart Addiction Assessment Services",
        "telephone": "+1-905-709-9985",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "8108 Yonge St Unit 3-4",
          "addressLocality": "Thornhill",
          "addressRegion": "ON",
          "postalCode": "L4J 1W4",
          "addressCountry": "CA"
        }
      }
    }
  });

  const selfAssessmentQuestions = [
    {
      category: 'Control & Compulsion',
      questions: [
        'Do you use drugs or alcohol more often or in larger amounts than you intended?',
        'Have you tried to cut down or stop using but been unable to?',
        'Do you spend a lot of time obtaining, using, or recovering from substance use?',
        'Do you experience strong cravings or urges to use drugs or alcohol?'
      ]
    },
    {
      category: 'Life Impact',
      questions: [
        'Has substance use interfered with your work, school, or home responsibilities?',
        'Have you given up important activities because of drug or alcohol use?',
        'Do you continue using despite problems it causes in relationships?',
        'Have you experienced legal problems related to substance use?'
      ]
    },
    {
      category: 'Physical & Psychological',
      questions: [
        'Do you need more of the substance to get the same effect (tolerance)?',
        'Do you experience withdrawal symptoms when you stop using?',
        'Do you use substances to avoid or relieve withdrawal symptoms?',
        'Has your physical or mental health declined due to substance use?'
      ]
    },
    {
      category: 'Risk & Safety',
      questions: [
        'Do you use substances in physically dangerous situations?',
        'Do you continue using despite knowing it worsens health problems?',
        'Have you engaged in risky behaviors while under the influence?',
        'Do you hide your substance use from family or friends?'
      ]
    }
  ];

  const warningSignsDetailed = [
    {
      icon: 'ri-alert-line',
      title: 'Loss of Control',
      description: 'Inability to limit substance use despite intentions to cut down or stop',
      examples: [
        'Using more than planned',
        'Failed attempts to quit',
        'Continuing despite promises to stop',
        'Unable to control when or how much you use'
      ],
      severity: 'high'
    },
    {
      icon: 'ri-time-line',
      title: 'Time Consumed',
      description: 'Significant time spent obtaining, using, or recovering from substances',
      examples: [
        'Planning activities around substance use',
        'Spending hours obtaining drugs',
        'Extended recovery periods',
        'Neglecting other activities'
      ],
      severity: 'medium'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Physical Dependence',
      description: 'Body adapts to the substance, requiring it to function normally',
      examples: [
        'Tolerance (needing more for same effect)',
        'Withdrawal symptoms when stopping',
        'Using to avoid withdrawal',
        'Physical discomfort without the substance'
      ],
      severity: 'high'
    },
    {
      icon: 'ri-emotion-unhappy-line',
      title: 'Neglecting Responsibilities',
      description: 'Failure to meet obligations at work, school, or home',
      examples: [
        'Missing work or school',
        'Poor performance',
        'Ignoring family duties',
        'Financial irresponsibility'
      ],
      severity: 'high'
    },
    {
      icon: 'ri-team-line',
      title: 'Relationship Problems',
      description: 'Continued use despite causing or worsening interpersonal conflicts',
      examples: [
        'Arguments about substance use',
        'Isolation from loved ones',
        'Broken trust',
        'Choosing substances over relationships'
      ],
      severity: 'medium'
    },
    {
      icon: 'ri-shield-cross-line',
      title: 'Risky Behavior',
      description: 'Using substances in dangerous situations or engaging in risky activities',
      examples: [
        'Driving under the influence',
        'Unsafe sexual behavior',
        'Mixing substances',
        'Using in hazardous environments'
      ],
      severity: 'high'
    }
  ];

  const whenToSeekHelp = [
    {
      icon: 'ri-alarm-warning-line',
      title: 'Immediate Help Needed',
      situations: [
        'Overdose symptoms (difficulty breathing, unconsciousness, blue lips)',
        'Severe withdrawal symptoms (seizures, hallucinations, confusion)',
        'Suicidal thoughts or self-harm',
        'Medical emergency related to substance use'
      ],
      action: 'Call 911 or go to emergency room immediately',
      color: 'red'
    },
    {
      icon: 'ri-phone-line',
      title: 'Professional Assessment',
      situations: [
        'You answered "yes" to 2 or more assessment questions',
        'Substance use is affecting your daily life',
        'You\'ve tried to quit but couldn\'t',
        'Family or friends have expressed concern'
      ],
      action: 'Contact our Thornhill clinic at (905) 709-9985',
      color: 'orange'
    },
    {
      icon: 'ri-heart-line',
      title: 'Prevention & Support',
      situations: [
        'You\'re concerned about your substance use',
        'You want to prevent escalation',
        'You need information about treatment options',
        'You\'re supporting someone with addiction'
      ],
      action: 'Schedule a confidential consultation',
      color: 'teal'
    }
  ];

  const faqs = [
    {
      question: 'How many "yes" answers indicate I have an addiction problem?',
      answer: 'According to the DSM-5 (Diagnostic and Statistical Manual of Mental Disorders), having 2-3 symptoms indicates mild substance use disorder, 4-5 indicates moderate, and 6 or more indicates severe substance use disorder. However, any "yes" answer suggests your substance use may be problematic and worth discussing with a healthcare professional. Don\'t wait for the problem to become severe - early intervention leads to better outcomes.'
    },
    {
      question: 'Can I be addicted even if I only use prescription medications as prescribed?',
      answer: 'Yes, physical dependence can develop even when taking medications exactly as prescribed, especially with opioids, benzodiazepines, and other controlled substances. Physical dependence means your body has adapted to the medication and you may experience withdrawal if you stop. This is different from addiction, which involves compulsive use despite harm. However, long-term use of certain medications requires medical supervision and may need gradual tapering under doctor\'s guidance.'
    },
    {
      question: 'What\'s the difference between physical dependence and addiction?',
      answer: 'Physical dependence is when your body adapts to a substance and experiences withdrawal symptoms when you stop. This can happen with many medications taken as prescribed. Addiction (substance use disorder) involves compulsive drug seeking and use despite harmful consequences, loss of control, and continued use despite problems. You can have physical dependence without addiction, but addiction typically includes physical dependence along with behavioral and psychological components.'
    },
    {
      question: 'I only use substances on weekends - can I still be addicted?',
      answer: 'Yes. Addiction is not defined by how often you use, but by the impact on your life and your ability to control use. Weekend-only use can still indicate addiction if you: experience cravings, plan your life around using, engage in risky behavior while using, continue despite negative consequences, or find it difficult to stop. The pattern and consequences matter more than the frequency.'
    },
    {
      question: 'What if I\'m not sure if I have a problem?',
      answer: 'If you\'re questioning whether you have a problem, that\'s already an important sign to pay attention to. Consider: Are you concerned enough to take this assessment? Have others expressed worry? Has substance use caused any negative consequences? Even if you\'re unsure, speaking with a healthcare professional can provide clarity. Our Thornhill clinic offers confidential assessments to help you understand your situation and explore options. Call (905) 709-9985 for a no-pressure consultation.'
    },
    {
      question: 'Will seeking help for addiction go on my permanent record?',
      answer: 'Medical treatment for addiction is confidential and protected by healthcare privacy laws (PHIPA in Ontario). Seeking help at our Thornhill addiction clinic will not appear on criminal records, employment records, or be shared without your consent. Your privacy is protected. Treatment is a medical matter, not a legal one. Don\'t let fear of stigma prevent you from getting the help you need.'
    },
    {
      question: 'What happens during an addiction assessment at your Thornhill clinic?',
      answer: 'Our confidential assessment includes: a private conversation with a healthcare professional about your substance use history, discussion of physical and mental health, review of how substance use affects your daily life, and exploration of your goals and concerns. We\'ll explain treatment options including methadone and Suboxone programs, answer your questions, and develop a personalized treatment plan if appropriate. There\'s no judgment - only support and professional guidance.'
    },
    {
      question: 'Can I get help if I\'m not ready to stop using completely?',
      answer: 'Yes. We understand that readiness for change varies. Harm reduction approaches can help you reduce risks even if you\'re not ready for complete abstinence. We can discuss safer use practices, overdose prevention, and gradual reduction strategies. Many people start with small steps and progress toward recovery at their own pace. Contact us to discuss your situation - we meet you where you are and support your journey toward better health.'
    }
  ];

  const handleCheckboxChange = (questionId: string) => {
    setCheckedItems(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getCheckedCount = () => {
    return Object.values(checkedItems).filter(Boolean).length;
  };

  const getResultMessage = () => {
    const count = getCheckedCount();
    if (count === 0) {
      return {
        title: 'No Immediate Concerns',
        message: 'Based on your responses, you may not currently have a substance use disorder. However, if you have any concerns about your substance use, we encourage you to speak with a healthcare professional.',
        color: 'green',
        icon: 'ri-checkbox-circle-line'
      };
    } else if (count <= 3) {
      return {
        title: 'Mild Concern - Consider Professional Guidance',
        message: 'Your responses suggest mild substance use concerns. Early intervention can prevent escalation. We recommend speaking with a healthcare professional about your substance use.',
        color: 'yellow',
        icon: 'ri-error-warning-line'
      };
    } else if (count <= 5) {
      return {
        title: 'Moderate Concern - Professional Help Recommended',
        message: 'Your responses indicate moderate substance use disorder symptoms. Professional treatment can help you regain control. Contact our Thornhill clinic for a confidential assessment.',
        color: 'orange',
        icon: 'ri-alert-line'
      };
    } else {
      return {
        title: 'Significant Concern - Immediate Professional Help Advised',
        message: 'Your responses suggest severe substance use disorder. Professional treatment is strongly recommended. Our team can provide comprehensive support including medication-assisted treatment. Please call us today.',
        color: 'red',
        icon: 'ri-alarm-warning-line'
      };
    }
  };

  const goToNextStep = () => {
    if (currentStep < selfAssessmentQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=thoughtful%20person%20looking%20at%20reflection%20in%20mirror%20with%20soft%20natural%20lighting%2C%20self-reflection%20and%20introspection%2C%20mental%20health%20awareness%2C%20teal%20and%20warm%20colors%2C%20contemplative%20mood%2C%20personal%20assessment%2C%20seeking%20help%2C%20compassionate%20healthcare%20perspective%2C%20clean%20modern%20aesthetic&width=1920&height=800&seq=are-you-addicted-hero-001&orientation=landscape"
            alt="Addiction self-assessment and awareness"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto">
            {/* Content */}
            <header className="text-center">
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-primary-500/20 backdrop-blur-md px-4 py-2 rounded-full mb-4">
                <i className="ri-question-line text-primary-300 text-lg"></i>
                <span className="text-primary-100 text-sm font-semibold uppercase tracking-wide">Self-Assessment</span>
              </div>

              {/* Breadcrumb */}
              <nav className="flex items-center justify-center mb-6" aria-label="Breadcrumb">
                <div className="flex items-center flex-wrap gap-1 sm:gap-2">
                  <button
                    onClick={() => navigate('/')}
                    className="flex items-center text-white/70 hover:text-white transition-colors cursor-pointer text-xs"
                    aria-label="Go to homepage"
                  >
                    <i className="ri-home-4-line text-sm"></i>
                    <span className="ml-1 font-medium">Home</span>
                  </button>
                  <i className="ri-arrow-right-s-line text-white/50 text-sm"></i>
                  <button
                    onClick={() => navigate('/addiction-services')}
                    className="text-white/70 hover:text-white transition-colors cursor-pointer text-xs font-medium"
                  >
                    Addiction Services
                  </button>
                  <i className="ri-arrow-right-s-line text-white/50 text-sm"></i>
                  <span className="text-primary-300 text-xs font-semibold">Are You Addicted?</span>
                </div>
              </nav>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Are You <strong>Addicted</strong>?
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
                Identify warning signs, assess your substance use, and learn when professional help is needed
              </p>

              {/* CTA Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <a
                  href="tel:9057099985"
                  className="bg-primary-500 text-white px-6 py-4 rounded-lg text-base font-semibold hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Call for confidential assessment"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Get Confidential Assessment
                </a>
                <button
                  onClick={() => {
                    const assessmentSection = document.getElementById('self-assessment');
                    if (assessmentSection) {
                      assessmentSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="bg-background-50 text-foreground-950 px-6 py-4 rounded-lg text-base font-semibold hover:bg-background-200/70 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Take self-assessment"
                >
                  <i className="ri-file-list-3-line mr-2"></i>
                  Take Self-Assessment
                </button>
              </div>
            </header>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground-950 mb-6">
              Understanding Your <strong>Substance Use</strong>
            </h2>
            <div className="space-y-4 text-lg text-foreground-700 leading-relaxed">
              <p>
                Recognizing <strong>addiction</strong> in yourself can be challenging. Denial, shame, and fear often prevent people from acknowledging they have a problem. This self-assessment can help you honestly evaluate your relationship with drugs or alcohol.
              </p>
              <p>
                <strong>Addiction</strong> is a medical condition, not a moral failing. If you identify warning signs, seeking help is a sign of strength, not weakness. Early intervention leads to better outcomes and faster recovery. Learn more about <a href="/what-is-addiction" className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors font-semibold">how addiction works in the brain</a> and why professional treatment matters.
              </p>
            </div>
          </div>

          <div className="bg-primary-50 border-l-4 border-teal-600 p-6 rounded-lg">
            <div className="flex items-start">
              <i className="ri-information-line text-primary-600 text-2xl mr-4 flex-shrink-0 mt-1"></i>
              <div>
                <h3 className="font-bold text-foreground-950 mb-2">Important Note</h3>
                <p className="text-foreground-700 leading-relaxed">
                  This self-assessment is for educational purposes only and does not replace professional diagnosis. If you have concerns about your substance use, please contact our <strong>Thornhill clinic</strong> at <a href="tel:9057099985" className="text-primary-600 font-semibold hover:underline">(905) 709-9985</a> for a confidential professional assessment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Self-Assessment Tool - Stepped Process */}
      <section id="self-assessment" className="py-16 sm:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground-950 mb-4">
              <strong>Addiction</strong> Self-Assessment
            </h2>
            <p className="text-lg text-foreground-600">
              Answer honestly - your responses are private and not stored
            </p>
          </header>

          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              {selfAssessmentQuestions.map((section, index) => (
                <div key={index} className="flex items-center flex-1">
                  <button
                    onClick={() => goToStep(index)}
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold transition-all cursor-pointer ${
                      index === currentStep
                        ? 'bg-primary-500 text-white scale-110'
                        : index < currentStep
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-300 text-foreground-600'
                    }`}
                    aria-label={`Go to step ${index + 1}: ${section.category}`}
                  >
                    {index < currentStep ? (
                      <i className="ri-check-line text-xl"></i>
                    ) : (
                      index + 1
                    )}
                  </button>
                  {index < selfAssessmentQuestions.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 transition-all ${
                        index < currentStep ? 'bg-primary-500' : 'bg-background-300'
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>

            {/* Step Labels */}
            <div className="flex items-center justify-between">
              {selfAssessmentQuestions.map((section, index) => (
                <div key={index} className="flex-1 text-center">
                  <p
                    className={`text-xs sm:text-sm font-semibold transition-colors ${
                      index === currentStep
                        ? 'text-primary-600'
                        : index < currentStep
                        ? 'text-primary-500'
                        : 'text-foreground-500'
                    }`}
                  >
                    {section.category}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Current Step Content */}
          <article className="bg-background-50 rounded-2xl p-6 sm:p-10 shadow-2xl">
            <div className="mb-8">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-primary-500 text-white rounded-full flex items-center justify-center mr-4 text-2xl font-bold">
                  {currentStep + 1}
                </div>
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-foreground-950">
                    {selfAssessmentQuestions[currentStep].category}
                  </h3>
                  <p className="text-sm text-foreground-600 mt-1">
                    Step {currentStep + 1} of {selfAssessmentQuestions.length}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {selfAssessmentQuestions[currentStep].questions.map((question, questionIndex) => {
                  const questionId = `q-${currentStep}-${questionIndex}`;
                  return (
                    <label
                      key={questionIndex}
                      className="flex items-start p-5 rounded-xl hover:bg-primary-50 transition-colors cursor-pointer border-2 border-transparent hover:border-teal-200"
                    >
                      <input
                        type="checkbox"
                        checked={checkedItems[questionId] || false}
                        onChange={() => handleCheckboxChange(questionId)}
                        className="w-6 h-6 text-primary-600 rounded border-background-300 focus:ring-teal-500 mt-0.5 cursor-pointer flex-shrink-0"
                      />
                      <span className="ml-4 text-base sm:text-lg text-foreground-700 leading-relaxed">
                        {question}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-background-200">
              <button
                onClick={goToPreviousStep}
                disabled={currentStep === 0}
                className={`flex items-center px-6 py-3 rounded-lg font-semibold transition-all ${
                  currentStep === 0
                    ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                    : 'bg-background-200 text-foreground-700 hover:bg-background-300 cursor-pointer'
                }`}
                aria-label="Previous step"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Previous
              </button>

              <div className="text-center">
                <p className="text-sm text-foreground-600">
                  <span className="font-bold text-primary-600 text-lg">{getCheckedCount()}</span> of{' '}
                  {selfAssessmentQuestions.reduce((acc, section) => acc + section.questions.length, 0)} checked
                </p>
              </div>

              {currentStep < selfAssessmentQuestions.length - 1 ? (
                <button
                  onClick={goToNextStep}
                  className="flex items-center px-6 py-3 rounded-lg font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-all cursor-pointer"
                  aria-label="Next step"
                >
                  Next
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              ) : (
                <button
                  onClick={() => {
                    const resultsSection = document.getElementById('assessment-results');
                    if (resultsSection) {
                      resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="flex items-center px-6 py-3 rounded-lg font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-all cursor-pointer"
                  aria-label="View results"
                >
                  View Results
                  <i className="ri-arrow-right-line ml-2"></i>
                </button>
              )}
            </div>
          </article>

          {/* Results */}
          {getCheckedCount() > 0 && (
            <div id="assessment-results" className="mt-12 scroll-mt-24">
              <div className={`bg-${getResultMessage().color}-50 border-l-4 border-${getResultMessage().color}-500 rounded-lg p-8`}>
                <div className="flex items-start">
                  <i className={`${getResultMessage().icon} text-${getResultMessage().color}-600 text-4xl mr-4 flex-shrink-0`}></i>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground-950 mb-3">
                      {getResultMessage().title}
                    </h3>
                    <p className="text-lg text-foreground-700 mb-4 leading-relaxed">
                      You checked {getCheckedCount()} out of {selfAssessmentQuestions.reduce((acc, section) => acc + section.questions.length, 0)} items.
                    </p>
                    <p className="text-foreground-700 leading-relaxed mb-6">
                      {getResultMessage().message}
                    </p>
                    <a
                      href="tel:9057099985"
                      className="inline-flex items-center bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-all cursor-pointer"
                    >
                      <i className="ri-phone-line mr-2"></i>
                      Call (905) 709-9985 for Help
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Warning Signs Detailed */}
      <section className="py-16 sm:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground-950 mb-4">
              Detailed Warning Signs of <strong>Addiction</strong>
            </h2>
            <p className="text-lg text-foreground-600 max-w-3xl mx-auto">
              Understanding these signs can help you recognize when substance use has become problematic
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {warningSignsDetailed.map((sign, index) => (
              <article 
                key={index}
                className="bg-background-100 rounded-2xl p-6 hover:shadow-lg transition-all"
              >
                <div className={`w-14 h-14 flex items-center justify-center bg-${sign.severity === 'high' ? 'red' : sign.severity === 'medium' ? 'orange' : 'yellow'}-100 rounded-xl mb-4`}>
                  <i className={`${sign.icon} text-3xl text-${sign.severity === 'high' ? 'red' : sign.severity === 'medium' ? 'orange' : 'yellow'}-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-foreground-950 mb-3">{sign.title}</h3>
                <p className="text-foreground-700 mb-4 leading-relaxed">{sign.description}</p>
                <ul className="space-y-2">
                  {sign.examples.map((example, idx) => (
                    <li key={idx} className="flex items-start text-sm text-foreground-600">
                      <i className="ri-arrow-right-s-line text-primary-600 mr-2 flex-shrink-0 mt-0.5"></i>
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* When to Seek Help */}
      <section className="py-16 sm:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground-950 mb-4">
              When to Seek <strong>Professional Help</strong>
            </h2>
            <p className="text-lg text-foreground-600">
              Different situations require different levels of intervention
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whenToSeekHelp.map((item, index) => (
              <article 
                key={index}
                className={`bg-background-50 rounded-2xl p-8 shadow-lg border-t-4 border-${item.color}-500`}
              >
                <div className={`w-16 h-16 flex items-center justify-center bg-${item.color}-100 rounded-full mb-6 mx-auto`}>
                  <i className={`${item.icon} text-4xl text-${item.color}-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-foreground-950 mb-4 text-center">{item.title}</h3>
                <ul className="space-y-3 mb-6">
                  {item.situations.map((situation, idx) => (
                    <li key={idx} className="flex items-start text-foreground-700">
                      <i className="ri-checkbox-circle-fill text-primary-600 mr-3 mt-1 flex-shrink-0"></i>
                      <span>{situation}</span>
                    </li>
                  ))}
                </ul>
                <div className={`bg-${item.color}-50 p-4 rounded-lg`}>
                  <p className={`text-sm font-semibold text-${item.color}-800`}>
                    <i className="ri-arrow-right-circle-line mr-2"></i>
                    {item.action}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground-950 mb-4">
              Frequently Asked <span className="text-primary-600">Questions</span>
            </h2>
            <p className="text-lg text-foreground-600">
              Common questions about recognizing and addressing addiction
            </p>
          </header>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-background-100 rounded-xl shadow-md overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-background-100 transition-colors cursor-pointer"
                  aria-expanded={expandedFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 
                    className="text-lg font-semibold text-foreground-950 pr-4"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-primary-600 text-2xl flex-shrink-0`}></i>
                </button>
                {expandedFaq === index && (
                  <div 
                    id={`faq-answer-${index}`}
                    className="px-6 pb-5"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="text-foreground-700 leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Take the Next Step?
          </h2>
          <p className="text-lg text-white/90 mb-10 leading-relaxed">
            If you recognize signs of <strong>addiction</strong> in yourself, professional help is available. Our <a href="/addiction-services" className="text-white hover:text-primary-100 underline underline-offset-2 transition-colors font-semibold">Thornhill addiction clinic</a> provides confidential assessments, <a href="/methadone-treatment" className="text-white hover:text-primary-100 underline underline-offset-2 transition-colors font-semibold">methadone</a> and <a href="/suboxone-treatment" className="text-white hover:text-primary-100 underline underline-offset-2 transition-colors font-semibold">Suboxone</a> treatment, and compassionate support. Serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, and the Greater Toronto Area.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
            <a
              href="tel:9057099985"
              className="bg-background-50 text-primary-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-background-200/70 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Call for confidential help"
            >
              <i className="ri-phone-line mr-2"></i>
              (905) 709-9985
            </a>
            <a
              href="/addiction-services"
              className="bg-primary-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-800 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="View treatment programs"
            >
              <i className="ri-heart-pulse-line mr-2"></i>
              Treatment Programs
            </a>
          </div>

          <div className="flex items-center justify-center space-x-2 text-white/80">
            <i className="ri-shield-check-line text-xl"></i>
            <p className="text-sm">Confidential, compassionate, professional care</p>
          </div>
        </div>
      </section>

      <MobileStickyCTA />
      <Footer />
    </div>
  );
}
